/**
 * FUNCIONES DE AUTENTICACIÓN CENTRALIZADAS
 * 
 * Lógica de autenticación reutilizable para todas las apps.
 * NO incluye UI, solo funciones puras.
 * 
 * @package @shared/auth
 */

import { supabase } from '../lib/supabaseClient';
import { PhoneAuthService } from '../services/phoneAuthService';
import { logger } from '../utils/logger';

/**
 * Iniciar sesión con email y contraseña
 */
export async function signInWithEmailPassword(email: string, password: string) {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      logger.error('Error en signInWithEmailPassword:', error);
      return { error };
    }

    return { data, error: null };
  } catch (error: any) {
    logger.error('Error inesperado en signInWithEmailPassword:', error);
    return { error: { message: error.message || 'Error inesperado' } };
  }
}

/**
 * Enviar código OTP por teléfono
 */
export async function sendPhoneOTP(phone: string) {
  try {
    const result = await PhoneAuthService.sendOTP(phone);
    return result;
  } catch (error: any) {
    logger.error('Error enviando OTP:', error);
    return {
      success: false,
      error: error.message || 'Error al enviar código OTP',
    };
  }
}

/**
 * Verificar código OTP y crear sesión.
 * Si se pasa email (flujo híbrido "Sin contraseña" con correo conocido), se actualiza esa cuenta con el teléfono verificado.
 */
export async function verifyPhoneOTP(phone: string, otpCode: string, email?: string) {
  try {
    const result = await PhoneAuthService.verifyOTP(phone, otpCode, email);
    return result;
  } catch (error: any) {
    logger.error('Error verificando OTP:', error);
    return {
      success: false,
      error: error.message || 'Error al verificar código OTP',
    };
  }
}

/**
 * Verificar si un email existe en auth.users (sin exponer información)
 * Usado para prevenir enumeración de usuarios
 */
async function verifyEmailExists(email: string): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('verificar_email_existe', {
      p_email: email.trim().toLowerCase(),
    });

    if (error) {
      logger.warn('Error verificando email:', error);
      // En caso de error, asumir que no existe para no enviar email
      return false;
    }

    return data && data.length > 0 && data[0]?.email_exists === true;
  } catch (error: any) {
    logger.warn('Error inesperado verificando email:', error);
    // En caso de error, asumir que no existe para no enviar email
    return false;
  }
}

export interface ResetPasswordOptions {
  /** App de origen para incluir en el enlace de recuperación (ej: empresa) */
  app?: string;
  /** Ruta de retorno en la app de origen (ej: /dashboard) */
  returnTo?: string;
  /** Teléfono opcional: si se proporciona, se envía además un OTP por WhatsApp */
  phone?: string;
}

/**
 * Enviar email de recuperación de contraseña y opcionalmente OTP por WhatsApp.
 * Solo envía si el usuario existe. Siempre retorna éxito para prevenir enumeración.
 * El enlace de recuperación incluye app y returnTo para que el usuario vuelva a la app de origen.
 */
export async function resetPassword(email: string, options?: ResetPasswordOptions) {
  try {
    const emailExists = await verifyEmailExists(email);
    if (!emailExists) {
      logger.info('Email no existe en base de datos, no se enviará email de recuperación');
      return { error: null };
    }

    const accountsUrl = import.meta.env.VITE_ACCOUNTS_URL || 'http://localhost:5176';
    const q = new URLSearchParams();
    if (options?.app) q.set('app', options.app);
    if (options?.returnTo) q.set('returnTo', options.returnTo);
    const redirectTo = q.toString()
      ? `${accountsUrl}/reset-password?${q.toString()}`
      : `${accountsUrl}/reset-password`;

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo,
    });

    if (error) {
      logger.error('Error en resetPassword:', error);
      return { error: null };
    }

    if (options?.phone?.trim()) {
      try {
        await PhoneAuthService.sendOTP(options.phone.trim());
        logger.info('OTP de recuperación enviado por WhatsApp');
      } catch (otpErr: any) {
        logger.warn('No se pudo enviar OTP de recuperación por WhatsApp:', otpErr?.message);
      }
    }

    logger.info('Email de recuperación enviado exitosamente');
    return { error: null };
  } catch (error: any) {
    logger.error('Error inesperado en resetPassword:', error);
    return { error: null };
  }
}

/**
 * Iniciar sesión con teléfono y contraseña
 * Busca el email asociado al teléfono y luego autentica
 */
export async function signInWithPhonePassword(phone: string, password: string) {
  try {
    // Buscar email asociado al teléfono
    const { data: emailData, error: emailError } = await supabase.rpc(
      'obtener_email_por_telefono',
      { p_telefono: phone.trim() }
    );

    if (emailError || !emailData || emailData.length === 0 || !emailData[0]?.email) {
      logger.error('Error obteniendo email por teléfono:', emailError?.message ?? emailError, emailError?.code, emailError?.details);
      return {
        error: {
          message: import.meta.env.DEV && emailError?.message
            ? `No se encontró cuenta o error: ${emailError.message}`
            : 'No se encontró una cuenta asociada a este teléfono',
        },
      };
    }

    const email = emailData[0].email;

    // Autenticar con email y contraseña
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      logger.error('Error en signInWithPhonePassword:', error);
      return { error };
    }

    return { data, error: null };
  } catch (error: any) {
    logger.error('Error inesperado en signInWithPhonePassword:', error);
    return { error: { message: error.message || 'Error inesperado' } };
  }
}

/**
 * Enviar códigos de login híbrido: un solo correo con magic link + OTP y el mismo OTP por WhatsApp/SMS.
 * La Edge Function send-phone-otp (con email y redirect_to) envía:
 * - Un email con el enlace para entrar y el código de 6 dígitos.
 * - El mismo código por teléfono (WhatsApp/SMS).
 * redirectTo: URL a la que Supabase redirigirá tras hacer clic en el enlace.
 */
export async function sendHybridLoginCodes(
  email: string,
  phone: string,
  redirectTo?: string
): Promise<{ successEmail: boolean; successPhone: boolean; error?: string }> {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const path = typeof window !== 'undefined' ? window.location.pathname || '/' : '/';
  const redirectUrl = redirectTo || `${origin}${path}`;

  const result = await PhoneAuthService.sendHybridOTP(
    phone,
    email.trim().toLowerCase(),
    redirectUrl
  );

  return {
    successEmail: result.success,
    successPhone: result.success,
    error: result.success ? undefined : (result.error || 'Error al enviar'),
  };
}

/**
 * Iniciar sesión con magic link (email)
 * Solo envía el magic link si el usuario existe en la base de datos
 * Siempre retorna éxito para prevenir enumeración de usuarios
 */
export async function signInWithMagicLink(email: string, redirectTo?: string) {
  try {
    // Verificar si el email existe antes de enviar
    const emailExists = await verifyEmailExists(email);
    
    if (!emailExists) {
      // Email no existe, pero retornamos éxito para no exponer información
      logger.info('Email no existe en base de datos, no se enviará magic link');
      return { data: null, error: null }; // Retornar éxito para prevenir enumeración
    }

    // Construir URL de redirección
    // Si no se proporciona redirectTo, usar el callback de la app actual
    const accountsUrl = import.meta.env.VITE_ACCOUNTS_URL || window.location.origin;
    const emailRedirectTo = redirectTo || `${accountsUrl}/auth/callback`;
    
    const { data, error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: emailRedirectTo,
      },
    });

    if (error) {
      logger.error('Error en signInWithMagicLink:', error);
      // Aún así retornamos error: null para prevenir enumeración
      return { data: null, error: null };
    }

    return { data, error: null };
  } catch (error: any) {
    logger.error('Error inesperado en signInWithMagicLink:', error);
    // Retornar error: null para prevenir enumeración
    return { data: null, error: null };
  }
}

/**
 * Construye la URL de redirect para SSO en accounts.
 * Agrega query params (returnTo, app, clientOrigin) a la ruta indicada.
 */
export function buildAccountsSsoRedirectUrl(
  path: string,
  options: {
    baseOrigin: string;
    returnTo?: string;
    app?: string;
    clientOrigin?: string;
  }
): string {
  const url = new URL(path, options.baseOrigin);
  if (options.returnTo) url.searchParams.set('returnTo', options.returnTo);
  if (options.app) url.searchParams.set('app', options.app);
  if (options.clientOrigin) url.searchParams.set('clientOrigin', options.clientOrigin);
  return url.toString();
}
